'use client';

import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils';

interface Slide {
  image: string;
  title: string;
  compactTitle: string;
  description: string;
  compactDescription: string;
  metric: string;
  metricLabel: string;
  subline: string;
  compactSubline: string;
  badge?: string;
  cta: string;
  compactCta: string;
}

const SLIDE_CHANGE_MS = 4500;

const slides: Slide[] = [
  {
    image: '/main.png',
    title: 'Домашний интернет и ТВ в Томске',
    compactTitle: 'Интернет и ТВ для дома',
    description: 'Подберём тариф под ваш адрес и быстро оформим подключение без лишних переплат.',
    compactDescription: 'Подберём тариф под ваш адрес без лишних переплат.',
    metric: 'до 1000',
    metricLabel: 'Мбит/с',
    subline: 'Подключение за 1–2 дня · поддержка 24/7',
    compactSubline: 'Подключение за 1–2 дня',
    badge: 'Подбор по адресу',
    cta: 'Подобрать тариф',
    compactCta: 'Тарифы',
  },
  {
    image: '/new.png',
    title: 'Стабильная скорость для дома',
    compactTitle: 'Стабильная скорость дома',
    description: 'Для работы, фильмов, игр и видеозвонков — без лагов и заметных просадок.',
    compactDescription: 'Для фильмов, работы, игр и видеозвонков.',
    metric: '1–2',
    metricLabel: 'дня',
    subline: 'Быстрое подключение и помощь с настройкой',
    compactSubline: 'Быстрое подключение',
    badge: 'Быстрое подключение',
    cta: 'Смотреть варианты',
    compactCta: 'Варианты',
  },
  {
    image: '/domru1.jpg',
    title: 'Проверка адреса за 2 минуты',
    compactTitle: 'Проверка адреса',
    description: 'Подскажем по роутеру, телевидению и доступным пакетам именно для вашего дома.',
    compactDescription: 'Подскажем по тарифу, роутеру и ТВ.',
    metric: '24/7',
    metricLabel: 'поддержка',
    subline: 'Поможем с оборудованием и подключением',
    compactSubline: 'Помощь с оборудованием',
    badge: 'Помощь с оборудованием',
    cta: 'Оставить заявку',
    compactCta: 'Заявка',
  },
];

function DotIndicators({ index, compact }: { index: number; compact: boolean }) {
  return (
    <div className="flex items-center gap-1.5">
      {slides.map((slide, i) => (
        <span
          key={slide.title + i}
          className={`rounded-full transition-all duration-300 ${
            i === index
              ? compact
                ? 'h-1.5 w-5 bg-rose-400'
                : 'h-1.5 w-6 bg-rose-400'
              : 'h-1.5 w-1.5 bg-white/35'
          }`}
        />
      ))}
    </div>
  );
}

export default function DomruBanner({ className }: { className?: string }) {
  const rootRef = useRef<HTMLAnchorElement | null>(null);
  const [index, setIndex] = useState(0);
  const [width, setWidth] = useState(340);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, SLIDE_CHANGE_MS);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const updateWidth = () => {
      setWidth(Math.round(el.clientWidth));
    };

    updateWidth();

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setWidth(Math.round(entry.contentRect.width));
    });

    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  const isUltra = width <= 240;
  const isCompact = width <= 280;

  const current = slides[index];
  const title = isCompact ? current.compactTitle : current.title;
  const description = isUltra ? '' : isCompact ? current.compactDescription : current.description;
  const subline = isUltra
    ? current.compactSubline
    : isCompact
      ? current.compactSubline
      : current.subline;
  const cta = isCompact ? current.compactCta : current.cta;

  return (
    <a
      ref={rootRef}
      href="https://tomsk-dom.ru/"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Перейти на tomsk-dom.ru"
      className={cn(
        'group relative block w-full overflow-hidden rounded-[24px] border border-slate-800/80 bg-slate-950 text-white shadow-[0_18px_50px_rgba(2,6,23,0.42)]',
        className,
      )}
    >
      <div className={`relative ${isUltra ? 'min-h-[240px]' : 'min-h-[268px]'}`}>
        {slides.map((slide, i) => (
          <div
            key={slide.title}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? 'opacity-100' : 'pointer-events-none opacity-0'
            }`}
          >
            <Image
              src={slide.image}
              alt={slide.title}
              fill
              quality={75}
              priority={i === 0}
              sizes="(max-width: 240px) 240px, (max-width: 280px) 280px, 340px"
              className={`object-cover transition-transform duration-[5500ms] ${
                i === index ? 'scale-105' : 'scale-100'
              }`}
            />

            <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-black/14 to-black/74" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.12),transparent_28%),radial-gradient(circle_at_bottom_left,rgba(244,63,94,0.14),transparent_36%)]" />
            <div className="absolute inset-0 shadow-[inset_0_-60px_80px_rgba(2,6,23,0.38)]" />
            <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
          </div>
        ))}

        <div
          className={`relative flex h-full min-h-inherit flex-col ${
            isUltra ? 'p-2' : isCompact ? 'p-2.5' : 'p-3'
          }`}
        >
          <div className={`flex items-center justify-between ${isUltra ? 'gap-1.5' : 'gap-2'}`}>
            <span
              className={`inline-flex rounded-full border border-white/10 bg-black/25 font-bold uppercase tracking-[0.14em] text-white/90 backdrop-blur-sm ${
                isUltra
                  ? 'px-2 py-1 text-[8px]'
                  : isCompact
                    ? 'px-2.5 py-1 text-[9px]'
                    : 'px-3 py-1 text-[10px]'
              }`}
            >
              Интернет + ТВ
            </span>

            {!isUltra && (
              <span
                className={`inline-flex rounded-full border border-white/10 bg-black/20 font-semibold text-rose-100 backdrop-blur-sm ${
                  isCompact ? 'px-2 py-1 text-[9px]' : 'px-2.5 py-1 text-[10px]'
                }`}
              >
                Реклама
              </span>
            )}
          </div>

          <div className={`mt-2 flex items-start justify-between ${isUltra ? 'gap-1.5' : 'gap-2'}`}>
            <div
              className={`inline-flex w-fit rounded-2xl border border-white/10 bg-black/20 backdrop-blur-sm ${
                isUltra ? 'px-2 py-1.5' : isCompact ? 'px-2.5 py-1.5' : 'px-3 py-2'
              }`}
            >
              <Image
                src="/logo.svg"
                alt="Логотип Дом.ру"
                width={156}
                height={60}
                className={`h-auto ${isUltra ? 'w-[78px]' : isCompact ? 'w-[90px]' : 'w-[104px]'}`}
                priority
              />
            </div>

            {!isCompact && current.badge && (
              <span className="inline-flex rounded-full border border-white/10 bg-white/[0.08] px-2.5 py-1 text-[9px] font-semibold leading-tight text-white/85 backdrop-blur-sm">
                {current.badge}
              </span>
            )}
          </div>

          <div
            className={`mt-auto rounded-[22px] border border-white/10 bg-slate-950/40 backdrop-blur-md shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] ${
              isUltra ? 'p-2' : isCompact ? 'p-2.5' : 'p-3'
            }`}
          >
            {isCompact ? (
              <div>
                <p
                  className={`overflow-hidden font-black leading-[1.08] text-white [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] ${
                    isUltra ? 'text-[13px]' : 'text-[14px]'
                  }`}
                >
                  {title}
                </p>

                {!!description && (
                  <p
                    className={`mt-1 leading-[1.28] text-white/85 ${
                      isUltra ? 'text-[10px]' : 'text-[10px]'
                    }`}
                  >
                    {description}
                  </p>
                )}

                <div className="mt-2 rounded-[16px] border border-white/10 bg-white/[0.07] px-2.5 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                  <div
                    className={`${isUltra ? 'text-[18px]' : 'text-[19px]'} font-black leading-none text-rose-300`}
                  >
                    {current.metric}
                  </div>
                  <div className="mt-1 text-[8px] font-semibold uppercase tracking-[0.04em] text-white/85">
                    {current.metricLabel}
                  </div>
                </div>

                <div className="mt-2 rounded-[16px] border border-white/10 bg-white/[0.05] px-2 py-2">
                  <div className="text-[9px] leading-tight text-white/78">{subline}</div>

                  <span className="mt-2 block w-full rounded-full bg-white px-2.5 py-1.5 text-center text-[9px] font-bold text-slate-950 transition-all duration-300 group-hover:bg-rose-50">
                    {cta}
                  </span>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="overflow-hidden text-[15px] font-black leading-[1.08] text-white [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                      {title}
                    </p>
                    <p className="mt-1 text-[11px] leading-[1.3] text-white/85">{description}</p>
                  </div>

                  <div className="shrink-0 rounded-2xl border border-white/10 bg-white/[0.07] px-3 py-2 text-right shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                    <div className="text-[21px] font-black leading-none text-rose-300">
                      {current.metric}
                    </div>
                    <div className="mt-1 text-[9px] font-semibold uppercase tracking-[0.04em] text-white/85">
                      {current.metricLabel}
                    </div>
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between gap-2 rounded-2xl border border-white/10 bg-white/[0.05] px-2.5 py-2">
                  <div className="text-[10px] leading-tight text-white/78">{subline}</div>

                  <span className="shrink-0 rounded-full bg-white px-3 py-1.5 text-[10px] font-bold text-slate-950 transition-all duration-300 group-hover:translate-x-0.5 group-hover:bg-rose-50">
                    {cta}
                  </span>
                </div>
              </div>
            )}

            <div className="mt-2 flex items-center justify-between gap-2">
              <DotIndicators index={index} compact={isCompact} />

              {!isCompact && (
                <span className="text-[10px] uppercase tracking-[0.12em] text-white/55">
                  tomsk-dom.ru
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </a>
  );
}
